import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Empty, Input, Modal, Select, Table, message } from 'antd';
import { Archive, Search, Send } from 'lucide-react';
import Layout from '../../Layout';
import { hrApi, useHr } from './hrStore';
import {
  OUTCOME, SOURCE, STAGE, branchById, deptOf, fmtD, normText, positionById, reqById, reqOpenSlots, talentPool, toDay, type PoolRow,
} from './hrLogic';
import { Badge, Chip, PageHeader, fmtDateTime, useHrPerms } from './hrUi';
import CandidateDrawer from './CandidateDrawer';

/** Kho ứng viên: hồ sơ tốt đã đóng (điểm cao, rút vì vị trí đủ người, tới bước offer…) để mời lại khi có vị trí mới */
export default function TalentPool() {
  const s = useHr();
  const { me, canRecruit } = useHrPerms();
  const [params, setParams] = useSearchParams();
  const go = (p: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    Object.entries(p).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setParams(next, { replace: true });
  };
  const today = toDay();
  const q = params.get('q') || '';
  const dept = params.get('dept') || '';
  const view = params.get('view') || 'ready';
  const id = params.get('id');
  const [invite, setInvite] = useState<PoolRow | null>(null);
  const [reqId, setReqId] = useState<string>();

  const rows = useMemo(() => talentPool(s, today), [s, today]);
  const deptOfReq = (rid?: string) => deptOf(s, { positionId: reqById(s, rid)?.positionId || '' });
  const deptOfRow = (r: PoolRow) => deptOfReq(r.c.requisitionId);
  const scoped = rows.filter(r => (!dept || deptOfRow(r) === dept) && (!q || normText(`${r.c.name} ${r.c.phone}`).includes(normText(q))));
  const list = scoped.filter(r => (view === 'invited' ? !!r.reinvited : view === 'saved' ? !!r.c.pool && !r.reinvited : !r.reinvited));
  const openReqs = s.requisitions.filter(r => r.status === 'approved' && reqOpenSlots(s, r) > 0);
  const reqLabel = (id?: string) => { const r = reqById(s, id); return r ? `${positionById(s, r.positionId)?.name} · ${branchById(s, r.branchId)?.short}` : ''; };

  const startInvite = (r: PoolRow) => {
    const old = reqById(s, r.c.requisitionId);
    const same = openReqs.find(x => x.positionId === old?.positionId) || openReqs.find(x => deptOf(s, x) === deptOfReq(old?.id));
    setReqId(same?.id);
    setInvite(r);
  };
  const doInvite = () => {
    if (!invite || !reqId) return;
    const res = hrApi.reinvite(invite.c.id, reqId, me);
    if (!res.ok) { Modal.error({ title: 'Chưa mời được', content: res.errors?.join('\n') }); return; }
    message.success(`Đã đưa ${invite.c.name} vào ${reqById(s, reqId)?.code}`);
    res.warnings?.forEach(w => message.warning(w));
    setInvite(null);
    go({ id: res.id! });
  };

  return (
    <Layout>
      <div className="flex-1 flex flex-col min-w-0 gap-4">
        <PageHeader title="Kho ứng viên" desc="Ứng viên tốt chưa trúng tuyển: điểm phỏng vấn từ 7, đã tới bước offer, rút vì vị trí đủ người… Dữ liệu giữ tối đa 12 tháng theo đồng ý của ứng viên" />
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-3 shadow-sm space-y-3">
          <div className="flex flex-wrap gap-2">
            <Input allowClear prefix={<Search size={14} className="text-gray-400" />} placeholder="Tên, số điện thoại" className="!w-[220px]" value={q} onChange={e => go({ q: e.target.value || null })} />
            <Select className="w-[200px]" value={dept} onChange={v => go({ dept: v || null })} options={[{ value: '', label: 'Tất cả bộ phận' }, ...s.depts.map(d => ({ value: d.id, label: d.name }))]} />
          </div>
          <div className="flex flex-wrap gap-1.5">
            <Chip active={view === 'ready'} onClick={() => go({ view: null })}>Có thể mời · {scoped.filter(r => !r.reinvited).length}</Chip>
            <Chip active={view === 'saved'} onClick={() => go({ view: 'saved' })}>HR đã lưu · {scoped.filter(r => r.c.pool && !r.reinvited).length}</Chip>
            <Chip active={view === 'invited'} onClick={() => go({ view: 'invited' })}>Đã mời lại · {scoped.filter(r => r.reinvited).length}</Chip>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm overflow-hidden">
          <Table size="middle" rowKey={r => r.c.id} dataSource={list} scroll={{ x: 1060 }} pagination={{ pageSize: 20, showSizeChanger: false, showTotal: t => `${t} ứng viên` }}
            onRow={r => ({ onClick: () => go({ id: r.reinvited?.id || r.c.id }), className: 'cursor-pointer' })}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Chưa có ứng viên trong kho" /> }}
            columns={[
              { title: 'Ứng viên', key: 'c', width: 210, render: (_, r) => <div><div className="font-medium">{r.c.name}</div><div className="text-[11px] text-gray-400">{r.c.phone} · {SOURCE[r.c.source]}</div></div> },
              { title: 'Từng ứng tuyển', key: 'req', width: 230, render: (_, r) => <div className="text-sm">{reqLabel(r.c.requisitionId)}<div className="text-[11px] text-gray-400">{reqById(s, r.c.requisitionId)?.code} · tới bước {STAGE[r.c.stage].short}</div></div> },
              { title: 'Điểm', key: 'score', width: 70, render: (_, r) => r.best ? <span className={r.best >= 7 ? 'text-emerald-600 font-semibold' : 'text-amber-600'}>{r.best}/10</span> : <span className="text-gray-300">—</span> },
              { title: 'Vì sao trong kho', key: 'why', width: 230, render: (_, r) => <div className="space-y-0.5"><div className="text-sm">{r.why}</div><div className="text-[11px] text-gray-400">{OUTCOME[r.c.outcome].label}{r.c.closeReason ? `: ${r.c.closeReason}` : ''} · {r.c.closedAt ? fmtDateTime(r.c.closedAt) : ''}</div></div> },
              { title: 'Dữ liệu còn giữ', key: 'exp', width: 120, render: (_, r) => <span className={r.expiresIn <= 30 ? 'text-amber-600' : 'text-gray-500'}>{r.expiresIn} ngày</span> },
              { title: '', key: 'act', width: 200, render: (_, r) => r.reinvited
                ? <div className="text-xs"><Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">Đã mời</Badge><div className="text-gray-400 mt-0.5">{reqLabel(r.reinvited.requisitionId)} · {STAGE[r.reinvited.stage].short}</div></div>
                : canRecruit && (
                  <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                    <Button size="small" type="primary" icon={<Send size={12} />} disabled={!openReqs.length} onClick={() => startInvite(r)}>Mời ứng tuyển</Button>
                    <Button size="small" type="text" icon={<Archive size={12} />} title={r.c.pool ? 'Bỏ khỏi kho' : 'Ẩn khỏi gợi ý'}
                      onClick={() => { hrApi.setPool(r.c.id, false, me); message.success('Đã bỏ khỏi kho'); }} aria-label="Bỏ khỏi kho" />
                  </div>
                ) },
            ]} />
        </div>
        <div className="text-[11px] text-gray-400">Không gồm ứng viên thuộc danh sách không tái tuyển, đang là nhân viên, đã xóa dữ liệu hoặc không đến nhận việc. Lưu thủ công một hồ sơ bằng nút “Lưu vào kho ứng viên” trong hồ sơ đã đóng.</div>
      </div>
      <CandidateDrawer id={id} onClose={() => go({ id: null })} />
      {invite && (
        <Modal open title={`Mời ${invite.c.name} ứng tuyển vị trí mới`} onCancel={() => setInvite(null)}
          footer={[<Button key="c" onClick={() => setInvite(null)}>Thôi</Button>, <Button key="s" type="primary" disabled={!reqId} onClick={doInvite}>Tạo hồ sơ ứng tuyển</Button>]}>
          <div className="space-y-3 text-sm">
            <div className="text-gray-500">Lần trước: {reqLabel(invite.c.requisitionId)} · tới bước {STAGE[invite.c.stage].label}{invite.best ? ` · điểm ${invite.best}/10` : ''} · {invite.c.closeReason}</div>
            <Select className="w-full" placeholder="Chọn yêu cầu đang tuyển" value={reqId} onChange={setReqId}
              options={openReqs.map(r => ({ value: r.id, label: `${r.code} · ${reqLabel(r.id)} · hạn ${fmtD(r.deadline)}` }))} />
            <div className="text-xs text-gray-400">Tạo hồ sơ mới ở bước Nhận hồ sơ, dùng lại thông tin và đồng ý xử lý dữ liệu đã có; hồ sơ cũ giữ nguyên để đối chiếu.</div>
          </div>
        </Modal>
      )}
    </Layout>
  );
}
